/**
 *  - The Chain
 * Main Application Component
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { StaffFunctionsProvider } from './context/StaffFunctionsContext';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { lazy, Suspense } from 'react';
const ShareRideLocation = lazy(() => import("./pages/ride/ShareRideLocation"));
const PudoOnboardLogin = lazy(() => import("./pages/pudoOnboard/PudoOnboardLogin"));
const PudoOnboardHome = lazy(() => import("./pages/pudoOnboard/PudoOnboardHome"));
const PudoOnboardWizard = lazy(() => import("./pages/pudoOnboard/PudoOnboardWizard"));
const PudoOnboardCandidateDetail = lazy(() => import("./pages/pudoOnboard/PudoOnboardCandidateDetail"));
const PudoOnboardSuccess = lazy(() => import("./pages/pudoOnboard/PudoOnboardSuccess"));
const PudoOperatorLogin = lazy(() => import("./pages/pudoOnboard/PudoOperatorLogin"));
const PudoOperatorStaffLogin = lazy(() => import("./pages/pudoOnboard/PudoOperatorStaffLogin"));
const PudoOperatorRaiseException = lazy(() => import("./pages/pudoOnboard/PudoOperatorRaiseException"));
const PudoOperatorExceptions = lazy(() => import("./pages/pudoOnboard/PudoOperatorExceptions"));
const PudoOperatorAudit = lazy(() => import("./pages/pudoOnboard/PudoOperatorAudit"));
const PudoOperatorStaff = lazy(() => import("./pages/pudoOnboard/PudoOperatorStaff"));
const PudoOperatorHome = lazy(() => import("./pages/pudoOnboard/PudoOperatorHome"));
const PudoOperatorReceive = lazy(() => import("./pages/pudoOnboard/PudoOperatorReceive"));
const PudoOperatorRelease = lazy(() => import("./pages/pudoOnboard/PudoOperatorRelease"));
// Phase F1 — Fast-access PUDO Agent portal
const PudoAgentLogin = lazy(() => import("./pages/pudoAgent/PudoAgentLogin"));
const PudoShareLocationCapture = lazy(() => import("./pages/PudoShareLocationCapture"));  // R18 - Remote PUDO Location Capture (public)
const KarishmaCustomerShareLocation = lazy(() => import("./pages/KarishmaCustomerShareLocation"));  // R30 B2 - Karishma -> Customer GPS share (public)
const ReceiverPin = lazy(() => import("./pages/ReceiverPin"));  // R30 B7e - public receiver PIN page (public)
const RateDelivery = lazy(() => import("./pages/RateDelivery"));  // public delivery rating + tip (email link)
const TipSuccess = lazy(() => import("./pages/TipSuccess"));  // public tip success/redirect target
const ReferAndEarn = lazy(() => import("./pages/ReferAndEarn"));  // in-app refer & earn dashboard
const PudoAgentHome = lazy(() => import("./pages/pudoAgent/PudoAgentHome"));
const PudoAgentWizard = lazy(() => import("./pages/pudoAgent/PudoAgentWizard"));
const DriverLeadWizard = lazy(() => import("./pages/pudoAgent/DriverLeadWizard"));
const DriverLeadSuccess = lazy(() => import("./pages/pudoAgent/DriverLeadSuccess"));
const SellerLeadWizard = lazy(() => import("./pages/pudoAgent/SellerLeadWizard"));
const SellerLeadSuccess = lazy(() => import("./pages/pudoAgent/SellerLeadSuccess"));
const PudoAgentLeads = lazy(() => import("./pages/pudoAgent/PudoAgentLeads"));
const PudoAgentLeadDetail = lazy(() => import("./pages/pudoAgent/PudoAgentLeadDetail"));
const AdminReleases = lazy(() => import("./pages/admin/AdminReleases"));
const AdminEnforcementActivity = lazy(() => import("./pages/admin/AdminEnforcementActivity"));
const AdminDailyDigest = lazy(() => import("./pages/admin/AdminDailyDigest"));
const OperatorFinance = lazy(() => import("./pages/operator/OperatorFinance"));
const OperatorHoldInvoices = lazy(() => import("./pages/operator/OperatorHoldInvoices"));
const AdminHoldStorage = lazy(() => import("./pages/admin/AdminHoldStorage"));
const AdminPudoDisputes = lazy(() => import("./pages/admin/AdminPudoDisputes"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));

const AdminPudoWithdrawals = lazy(() => import("./pages/admin/AdminPudoWithdrawals"));
const PudoAgentDrafts = lazy(() => import("./pages/pudoAgent/PudoAgentDrafts"));
const PudoAgentSubmitted = lazy(() => import("./pages/pudoAgent/PudoAgentSubmitted"));
const PudoAgentSuccess = lazy(() => import("./pages/pudoAgent/PudoAgentSuccess"));
const AdminPudoOnboarding = lazy(() => import("./pages/admin/AdminPudoOnboarding"));
const AdminPudoOnboardingAgents = lazy(() => import("./pages/admin/AdminPudoOnboardingAgents"));
const SavedRideAddresses = lazy(() => import("./pages/ride/SavedRideAddresses"));

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';

// Main Pages
import Dashboard from './pages/Dashboard';
import MarkApprovals from './pages/MarkApprovals';
import { SetPasswordPrompt } from './components/SetPasswordPrompt';
import AdminPanel from './pages/AdminPanel';
import WelcomeContentEditor from './pages/admin/WelcomeContentEditor';
import AdminShippingCalculator from './pages/admin/ShippingCalculator';
import Welcome from './pages/Welcome';

// Pilot Pages (go.liantiaoja.com)
import { PilotWelcome, ModernWelcome, PilotBulkyDelivery, PilotContractHire, PilotOpsPortal } from './pages/pilot';
import BookRide from './pages/BookRide';

// Isolated Ride Pages (does not touch package system)
import StandardRide from './pages/ride/StandardRide';
import MapBookRide from './pages/ride/MapBookRide';
import RideRouter from "./pages/ride/RideRouter";

// Feature flag: set to true to enable the new map-first booking at /ride/book
const ENABLE_MAP_BOOKING = true;
import ScheduledRide from './pages/ride/ScheduledRide';
import AirportRide from './pages/ride/AirportRide';
import RidePaymentSuccess from './pages/ride/PaymentSuccess';
import RidePaymentCancel from './pages/ride/PaymentCancel';
import RideTracking from './pages/ride/RideTracking';
import MyApplications from './pages/MyApplications';

// Driver Pages
import DriverDashboard from './pages/DriverDashboard';
import DriverRegister from './pages/DriverRegister';
import DriverHome from './pages/DriverHome';
import DriverScan from './pages/DriverScan';
import DriverScanNew from './pages/DriverScanNew';
import DriverEarnings from './pages/DriverEarnings';
import MerchantEarnings from './pages/MerchantEarnings';
import Withdrawals from './pages/Withdrawals';
import GlobalPwaInstallPrompt from './components/GlobalPwaInstallPrompt';
import DriverWallet from './pages/DriverWallet';
import DriverFleetMileage from './pages/driver/DriverFleetMileage';
import AdminDriverPayouts from './pages/Admin/AdminDriverPayouts';
import AdminFleetDashboard from './pages/admin/AdminFleetDashboard';
import DriverVehiclePhotos from './pages/DriverVehiclePhotos';

// PUDO Pages
import PudoDashboard from './pages/PudoDashboard';
import PudoReceive from './pages/PudoReceive';
import PudoHandoff from './pages/PudoHandoff';
import PudoRelease from './pages/PudoRelease';
import PudoInventory from './pages/PudoInventory';

// Merchant Fulfillment
import MerchantFulfillment from './pages/MerchantFulfillment';

// Role Guards
import { DriverRoute, PudoRoute, MerchantRoute, FulfillmentRoute } from './components/RoleRoute';
import AdminFinance from './pages/AdminFinance';
import CEODashboard from './pages/CEODashboard';
import PackageTracking from './pages/PackageTracking';
import DriverRewards from './pages/DriverRewards';
import CustomerRewards from './pages/CustomerRewards';
import AdminIncentives from './pages/AdminIncentives';
import TrustDashboard from './pages/TrustDashboard';
import VerifyIdentity from './pages/VerifyIdentity';
import SmartRoutingDashboard from './pages/SmartRoutingDashboard';
import StorePage from './pages/StorePage';

// Messaging & Support Pages (Phase 11 Part 2)
import MessagesPage from './pages/MessagesPage';
import SupportCenter from './pages/SupportCenter';
import SupportDashboard from './pages/SupportDashboard';

// Business Pages
import BusinessDashboard from './pages/BusinessDashboard';
import BusinessRegister from './pages/BusinessRegister';
import BusinessApplicationForm from './pages/BusinessApplicationForm';

// Merchant Store Management
import StoreSettings from './pages/StoreSettings';
import SellerDiscounts from './pages/SellerDiscounts';
import AdminApplicationReview from './pages/AdminApplicationReview';
import ProductManagement from './pages/ProductManagement';

// Enhanced Application Forms
import DriverApplicationForm from './pages/DriverApplicationForm';

// Admin Management Pages
import AdminFeaturedStores from './pages/AdminFeaturedStores';
import AdminDriverManagement from './pages/AdminDriverManagement';
import AdminBusinessManagement from './pages/AdminBusinessManagement';
import AdminInventoryManagement from './pages/AdminInventoryManagement';
import AdminAuditLogs from './pages/AdminAuditLogs';
import AdminPricingManagement from './pages/AdminPricingManagement';

// Settings Page
import SettingsPage from './pages/SettingsPage';

// Staff & Products Management
import AdminStaffManagement from './pages/AdminStaffManagement';
import AdminProductsManagement from './pages/AdminProductsManagement';
import AdminCategoryManagement from './pages/AdminCategoryManagement';

// Push Notifications
import AdminPushNotifications from './pages/AdminPushNotifications';

// QR Code Printing
import QRCodePrinting from './pages/QRCodePrinting';

// Package Pages
import SendPackage from './pages/SendPackage';
import MyPackages from './pages/MyPackages';

// Marketplace Pages
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import MyOrders from './pages/MyOrders';

// Additional Pages from Previous Work
import DriverMobileHub from './pages/DriverMobileHub';
import OrderTrackingMap from './pages/OrderTrackingMap';
import LoyaltyProgram from './pages/LoyaltyProgram';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ForgotPassword from './pages/ForgotPassword';
import GuestCheckout from './pages/GuestCheckout';
import ResetPassword from './pages/ResetPassword';
import AdminPasswordResets from './pages/AdminPasswordResets';
import AdminFulfillmentDashboard from './pages/AdminFulfillmentDashboard';
import AdminUserManagement from './pages/AdminUserManagement';
import MyReviews from './pages/MyReviews';
import MyDisputes from './pages/MyDisputes';
import AdminDisputesEnhanced from './pages/AdminDisputesEnhanced';
import ProfileSettings from './pages/ProfileSettings';
import FirstTimeSetup from './pages/FirstTimeSetup';
// Agent Performance and KPI Dashboards
import AgentPerformance from "./pages/Admin/AgentPerformance";
import AdminPudoStations from "./pages/Admin/AdminPudoStations";
import AdminFieldAgents from "./pages/Admin/AdminFieldAgents";
import AdminBusinessQuick from "./pages/Admin/AdminBusinessQuick";
import Hub from "./pages/Hub";
import QuickPin from "./pages/QuickPin";
import KarishmaPortal from "./pages/KarishmaPortal";
// R30 B6.2.5 -- read-only reconciliation tab
const KarishmaPayments = lazy(() => import("./pages/KarishmaPayments"));
import QuickPinReset from "./pages/QuickPinReset";
const AdminVehicleOps = lazy(() => import("./pages/Admin/AdminVehicleOps"));  // R19
const DriverVehicleLog = lazy(() => import("./pages/DriverVehicleLog"));  // R20
import FieldAgentBusinessQuick from "./pages/portalFieldAgent/FieldAgentBusinessQuick";
import AdminFieldAgentProductivity from "./pages/Admin/AdminFieldAgentProductivity";
import FieldAgentLogEntry from "./pages/portalFieldAgent/FieldAgentLogEntry";
import FieldAgentWeeklyRollup from "./pages/portalFieldAgent/FieldAgentWeeklyRollup";
import AdminFieldAgentWeeklyRollup from "./pages/Admin/AdminFieldAgentWeeklyRollup";
import AdminFieldAgentActivityDetail from "./pages/Admin/AdminFieldAgentActivityDetail";
import AdminFieldActivityReviewQueue from "./pages/Admin/AdminFieldActivityReviewQueue";
import { BusinessQuickLogin, BusinessQuickBooking, BusinessQuickDashboard, BusinessQuickPackageDetail } from "./pages/BusinessQuick/BusinessQuickPages";
import FieldAgentActivityDetail from "./pages/portalFieldAgent/FieldAgentActivityDetail";
import AssistedBookingKPI from "./pages/Admin/AssistedBookingKPI";
import AdminLocationIntelligence from "./pages/admin/AdminLocationIntelligence";
import AdminLocationCapture from "./pages/admin/AdminLocationCapture";
import DisputesManagement from "./pages/Admin/DisputesManagement";
import AgentLogin from "./pages/admin/AgentLogin";
import AssistedBookingConsole from "./pages/admin/AssistedBookingConsole";
import AssistedBookingAgents from "./pages/admin/AssistedBookingAgents";
import AgentBookingConsole from "./pages/admin/AgentBookingConsole";

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowFirstTimeSetup = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to first-time setup if password change required (unless already on that page)
  if (user?.must_change_password && !allowFirstTimeSetup) {
    return <Navigate to="/first-time-setup" replace />;
  }
  
  return children;
};

// Admin Route Component - Now allows staff with assigned functions
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, hasRole, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to first-time setup if password change required
  if (user?.must_change_password) {
    return <Navigate to="/first-time-setup" replace />;
  }
  
  // Allow admins, CEOs, and staff
  if (!isAdmin() && !hasRole('STAFF')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Subdomain detection helper
const getSubdomain = () => {
  const hostname = window.location.hostname;
  // Handle production subdomains
  if (hostname.startsWith('go.')) return 'go';
  if (hostname.startsWith('admin.')) return 'admin';
  if (hostname.startsWith('fulfillment.')) return 'fulfillment';
  // Check for URL params for testing in preview
  const urlParams = new URLSearchParams(window.location.search);
  const subdomainParam = urlParams.get('subdomain');
  if (subdomainParam) return subdomainParam;
  return null;
};

// Public Route (redirect to appropriate dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Check if user must change password first (first-time login)
    if (user?.must_change_password) {
      return <Navigate to="/first-time-setup" replace />;
    }
    
    // PRIORITY: Check for redirect (query param OR sessionStorage)
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get("redirect") || sessionStorage.getItem("post_login_redirect");
    if (redirectUrl) {
      sessionStorage.removeItem("post_login_redirect");
      return <Navigate to={redirectUrl} replace />;
    }
    
    // Check subdomain for redirect target
    const subdomain = getSubdomain();
    if (subdomain === 'admin' && isAdmin()) {
      return <Navigate to="/admin" replace />;
    }
    if (subdomain === 'fulfillment') {
      return <Navigate to="/pudo" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Subdomain Router - redirects based on subdomain after login
const SubdomainRedirect = () => {
  const { isAuthenticated, loading, hasRole } = useAuth();
  const subdomain = getSubdomain();
  
  // For 'go' subdomain (pilot site), show ModernWelcome directly without auth
  if (subdomain === 'go') {
    return <ModernWelcome />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Route based on subdomain
  if (subdomain === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  if (subdomain === 'fulfillment') {
    // Check if user has fulfillment/pudo role
    if (hasRole && (hasRole('FULFILLMENT') || hasRole('PUDO_POINT'))) {
      return <Navigate to="/pudo" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  const subdomain = getSubdomain();
  
  return (
    <Routes>
      <Route path="/rate/:trackingId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><RateDelivery /></Suspense>} />
      <Route path="/tip-success" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><TipSuccess /></Suspense>} />
      <Route path="/refer" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><ReferAndEarn /></Suspense>} />
      {/* Subdomain root redirect */}
      {subdomain && (
        <Route path="/" element={<SubdomainRedirect />} />
      )}
      
      {/* Auth Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={<ForgotPassword />}
      />
      <Route 
        path="/reset-password" 
        element={<ResetPassword />}
      />
      <Route 
        path="/first-time-setup" 
        element={
          <ProtectedRoute allowFirstTimeSetup={true}>
            <FirstTimeSetup />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      
      {/* Main Dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Panel */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        } 
      />
      <Route path="/admin/welcome-editor" element={<AdminRoute><WelcomeContentEditor /></AdminRoute>} />
      <Route path="/admin/releases" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminReleases /></Suspense></AdminRoute>} />
      <Route path="/admin/enforcement-activity" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminEnforcementActivity /></Suspense></AdminRoute>} />
      <Route path="/admin/daily-digest" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminDailyDigest /></Suspense></AdminRoute>} />
      <Route path="/portal/pudo-onboard/operator/finance" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><OperatorFinance /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/hold-invoices" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><OperatorHoldInvoices /></Suspense>} />
      <Route path="/admin/pudo-withdrawals" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminPudoWithdrawals /></Suspense></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminReports /></Suspense></AdminRoute>} />
      <Route path="/admin/pudo-disputes" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminPudoDisputes /></Suspense></AdminRoute>} />
      <Route path="/admin/hold-storage" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminHoldStorage /></Suspense></AdminRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute requiredRole="admin"><AdminApplicationReview /></ProtectedRoute>} />
      <Route path="/admin/shipping" element={<ProtectedRoute requiredRole="admin"><AdminShippingCalculator /></ProtectedRoute>} />



      
      {/* Admin Featured Stores Management */}
      <Route 
        path="/admin/featured-stores" 
        element={
          <AdminRoute>
            <AdminFeaturedStores />

          </AdminRoute>
        } 
      />
      
      {/* Admin Driver Management */}
      <Route 
        path="/admin/drivers" 
        element={
          <AdminRoute>
            <AdminDriverManagement />
          </AdminRoute>
        } 
      />
      
      {/* Admin Business/PUDO Management */}
      <Route 
        path="/admin/businesses" 
        element={
          <AdminRoute>
            <AdminBusinessManagement />
          </AdminRoute>
        } 
      />
      
      {/* Admin Inventory Management */}
      <Route 
        path="/admin/inventory" 
        element={
          <AdminRoute>
            <AdminInventoryManagement />
          </AdminRoute>
        } 
      />
      
      {/* Admin Audit Logs */}
      <Route 
        path="/admin/audit-logs" 
        element={
          <AdminRoute>
            <AdminAuditLogs />
          </AdminRoute>
        } 
      />
      
      {/* Admin Pricing & Promotions */}
      <Route 
        path="/admin/pricing" 
        element={
          <AdminRoute>
            <AdminPricingManagement />
          </AdminRoute>
        } 
      />
      
      {/* Admin Staff Management */}
      <Route 
        path="/admin/staff" 
        element={
          <AdminRoute>
            <AdminStaffManagement />
          </AdminRoute>
        } 
      />
      
      {/* Admin Products Management */}
      <Route 
        path="/admin/products" 
        element={
          <AdminRoute>
            <AdminProductsManagement />
      /* Admin Category Management */
      <Route 
        path="/admin/categories" 
        element={
          <AdminRoute>
            <AdminCategoryManagement />
          </AdminRoute>
        } 
      />
          </AdminRoute>
        } 
      />
      
      {/* Admin Push Notifications */}
      <Route 
        path="/admin/notifications" 
        element={
          <AdminRoute>
            <AdminPushNotifications />
          </AdminRoute>
        } 
      />
      
      {/* QR Code Printing */}
      <Route 
        path="/admin/qr-codes" 
        element={
          <AdminRoute>
            <QRCodePrinting />
          </AdminRoute>
        } 
      />
      
      {/* Driver Routes */}
      <Route 
        path="/driver" 
        element={
          <DriverRoute>
            <DriverHome />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/home" 
        element={
          <DriverRoute>
            <DriverHome />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/old" 
        element={
          <DriverRoute>
            <DriverDashboard />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/register" 
        element={
          <ProtectedRoute>
            <DriverRegister />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/driver/scan" 
        element={
          <DriverRoute>
            <DriverScanNew />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/scan/old" 
        element={
          <DriverRoute>
            <DriverScan />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/earnings" 
        element={
          <DriverRoute>
            <DriverEarnings />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/withdrawals" 
        element={
          <DriverRoute>
            <Withdrawals />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/wallet" 
        element={
          <DriverRoute>
            <DriverWallet />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/fleet/mileage" 
        element={
          <DriverRoute>
            <DriverFleetMileage />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/vehicle-photos" 
        element={
          <DriverRoute>
            <DriverVehiclePhotos />
          </DriverRoute>
        } 
      />
      <Route 
        path="/driver/rewards" 
        element={
          <DriverRoute>
            <DriverRewards />
          </DriverRoute>
        } 
      />
      
      {/* PUDO Routes */}
      <Route 
        path="/pudo" 
        element={
          <PudoRoute>
            <PudoDashboard />
          </PudoRoute>
        } 
      />
      <Route 
        path="/pudo/receive" 
        element={
          <PudoRoute>
            <PudoReceive />
          </PudoRoute>
        } 
      />
      <Route 
        path="/pudo/handoff" 
        element={
          <PudoRoute>
            <PudoHandoff />
          </PudoRoute>
        } 
      />
      <Route 
        path="/pudo/release" 
        element={
          <PudoRoute>
            <PudoRelease />
          </PudoRoute>
        } 
      />
      <Route 
        path="/pudo/inventory" 
        element={
          <PudoRoute>
            <PudoInventory />
          </PudoRoute>
        } 
      />
      
      {/* Customer Rewards */}
      <Route 
        path="/rewards" 
        element={
          <ProtectedRoute>
            <CustomerRewards />
          </ProtectedRoute>
        } 
      />
      
      {/* Merchant Routes */}
      <Route 
        path="/merchant/earnings" 
        element={
          <MerchantRoute>
            <MerchantEarnings />
          </MerchantRoute>
        } 
      />
      <Route 
        path="/merchant/fulfillment" 
        element={
          <FulfillmentRoute>
            <MerchantFulfillment />
          </FulfillmentRoute>
        } 
      />
      <Route 
        path="/business/fulfillment" 
        element={
          <FulfillmentRoute>
            <MerchantFulfillment />
          </FulfillmentRoute>
        } 
      />
      
      {/* Admin Finance Route */}
      <Route 
        path="/admin/finance" 
        element={
          <ProtectedRoute>
            <AdminFinance />
      <Route 
        path="/admin/driver-payouts" 
        element={
          <AdminRoute>
            <AdminDriverPayouts />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/fleet" 
        element={
          <AdminRoute>
            <AdminFleetDashboard />
          </AdminRoute>
        } 
      />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Incentives Route */}
      <Route 
        path="/admin/incentives" 
        element={
          <ProtectedRoute>
            <AdminIncentives />
          </ProtectedRoute>
        } 
      />
      
      {/* Trust & Safety Dashboard */}
      <Route 
        path="/admin/trust" 
        element={
          <ProtectedRoute>
            <TrustDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Identity Verification */}
      <Route 
        path="/verify" 
        element={
          <ProtectedRoute>
            <VerifyIdentity />
          </ProtectedRoute>
        } 
      />
      
      {/* Smart Routing Dashboard */}
      <Route 
        path="/admin/routing" 
        element={
          <ProtectedRoute>
            <SmartRoutingDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Store Page (Phase 11) */}
      <Route path="/store/:merchantId" element={<StorePage />} />
      
      {/* CEO Dashboard Route */}
      <Route 
        path="/ceo" 
        element={
          <ProtectedRoute>
            <CEODashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/executive" 
        element={
          <ProtectedRoute>
            <CEODashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Business Routes */}
      <Route 
        path="/business" 
        element={
          <ProtectedRoute>
            <BusinessDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/business/register" 
        element={
          <ProtectedRoute>
            <BusinessRegister />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/business/apply" 
        element={<BusinessApplicationForm />} 
      />
      <Route 
        path="/pudo/apply" 
        element={<BusinessApplicationForm />} 
      />
      
      {/* Merchant Store Management */}
      <Route 
        path="/merchant/store/settings" 
        element={
          <MerchantRoute>
            <StoreSettings />
          </MerchantRoute>
        } 
      />
      {/* Seller Discounts Management */}
      <Route 
        path="/seller/discounts" 
        element={
          <ProtectedRoute>
            <SellerDiscounts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/merchant/products" 
        element={
          <MerchantRoute>
            <ProductManagement />
          </MerchantRoute>
        } 
      />
      <Route 
        path="/merchant/products/new" 
        element={
          <MerchantRoute>
            <ProductManagement />
          </MerchantRoute>
        } 
      />
      <Route 
        path="/merchant/products/:productId/edit" 
        element={
          <MerchantRoute>
            <ProductManagement />
          </MerchantRoute>
        } 
      />
      
      {/* Enhanced Driver Application */}
      <Route 
        path="/driver/apply" 
        element={<DriverApplicationForm />} 
      />
      <Route path="/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
      
      {/* Package Routes */}
      <Route 
        path="/send" 
        element={<SendPackage />} 
      />
      <Route 
        path="/packages" 
        element={
          <ProtectedRoute>
            <MyPackages />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/track/:packageId" 
        element={
          <ProtectedRoute>
            <PackageTracking />
          </ProtectedRoute>
        } 
      />
      {/* Public Tracking (no auth required) */}
      <Route 
        path="/tracking/:packageId" 
        element={<PackageTracking />} 
      />
      
      {/* Marketplace Routes */}
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route 
        path="/cart" 
        element={
          <ProtectedRoute>
            <Cart />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/checkout"
      />
      <Route
        path="/guest-checkout"
        element={<GuestCheckout />}
      />
      <Route
        path="/checkout-old" 
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/orders" 
        element={
          <ProtectedRoute>
            <MyOrders />
          </ProtectedRoute>
        } 
      />
      
      {/* Messaging & Support (Phase 11 Part 2) */}
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/support" 
        element={
          <ProtectedRoute>
            <SupportCenter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/support" 
        element={
          <AdminRoute>
            <SupportDashboard />
          </AdminRoute>
        } 
      />
      
      {/* Settings Page */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Additional Routes from Previous Work */}
      <Route 
        path="/driver/hub" 
        element={
          <DriverRoute>
            <DriverMobileHub />
          </DriverRoute>
        } 
      />
      <Route 
        path="/track-order/:orderId" 
        element={
          <ProtectedRoute>
            <OrderTrackingMap />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rewards" 
        element={
          <ProtectedRoute>
            <LoyaltyProgram />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/analytics" 
        element={
          <AdminRoute>
            <AnalyticsDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/password-resets" 
        element={
          <AdminRoute>
            <AdminPasswordResets />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/fulfillment" 
        element={
          <AdminRoute>
            <AdminFulfillmentDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <AdminRoute>
            <AdminUserManagement />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/disputes" 
        element={
          <AdminRoute>
            <AdminDisputesEnhanced />
          </AdminRoute>
        } 
      />
      <Route 
        path="/my-reviews" 
        element={
          <ProtectedRoute>
            <MyReviews />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-disputes" 
        element={
          <ProtectedRoute>
            <MyDisputes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        } 
      />
      
      {/* Welcome Screen — GO Platform shows ModernWelcome */}
      <Route 
        path="/welcome" 
        element={
          <PublicRoute>
            <ModernWelcome />
          </PublicRoute>
        } 
      />
      
      {/* Redirects */}
      
      {/* Ride Routes - Separate from Package Domain */}
      <Route path="/rides" element={<RideRouter />} />
      <Route path="/ride" element={<RideRouter />} />
      <Route path="/ride/book" element={ENABLE_MAP_BOOKING ? <MapBookRide /> : <BookRide />} />

      {/* Isolated Ride Booking Pages */}
      <Route path="/ride/standard" element={<StandardRide />} />
      <Route path="/ride/schedule" element={<ProtectedRoute><ScheduledRide /></ProtectedRoute>} />
      <Route path="/ride/airport" element={<AirportRide />} />
      <Route path="/ride/payment/success" element={<ProtectedRoute><RidePaymentSuccess /></ProtectedRoute>} />
      <Route path="/ride/payment/cancel" element={<RidePaymentCancel />} />
      <Route path="/ride/track/:rideId" element={<ProtectedRoute><RideTracking /></ProtectedRoute>} />
      <Route path="/ride/share-location/:token" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>}><ShareRideLocation /></Suspense>} />
      <Route path="/share-location/:token" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>}><ShareRideLocation /></Suspense>} />
      <Route path="/customer/share-location/:token" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><KarishmaCustomerShareLocation /></Suspense>} />
      <Route path="/d/:token" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-900"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><ReceiverPin /></Suspense>} />

      <Route path="/pilot" element={<ModernWelcome />} />
      <Route path="/pilot/classic" element={<PilotWelcome />} />
      <Route path="/" element={<ModernWelcome />} />
            <Route path="/pudo/share-location/:token" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoShareLocationCapture /></Suspense>} />
            <Route path="/portal/pudo-agent" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoAgentLogin /></Suspense>} />
      <Route path="/portal/pudo-agent/home" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoAgentHome /></Suspense>} />
      <Route path="/portal/pudo-agent/new" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoAgentWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/edit/:pudoId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoAgentWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/drafts" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>}><PudoAgentDrafts /></Suspense>} />
      <Route path="/portal/pudo-agent/submitted" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoAgentSubmitted /></Suspense>} />
      <Route path="/portal/pudo-agent/driver/new" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><DriverLeadWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/driver/edit/:draftId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><DriverLeadWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/driver/success/:applicationId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><DriverLeadSuccess /></Suspense>} />
      <Route path="/portal/pudo-agent/seller/new" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" /></div>}><SellerLeadWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/seller/edit/:applicationId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" /></div>}><SellerLeadWizard /></Suspense>} />
      <Route path="/portal/pudo-agent/seller/success/:applicationId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" /></div>}><SellerLeadSuccess /></Suspense>} />
      <Route path="/portal/pudo-agent/leads" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><PudoAgentLeads /></Suspense>} />
      <Route path="/portal/pudo-agent/leads/:leadType/:leadId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><PudoAgentLeadDetail /></Suspense>} />
      <Route path="/portal/pudo-agent/success/:pudoId" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoAgentSuccess /></Suspense>} />
      <Route path="/portal/field-agent/log-entry" element={<FieldAgentLogEntry />} />
      <Route path="/portal/field-agent/log-entry/:id" element={<FieldAgentLogEntry />} />
      <Route path="/admin/field-agents/:agent_id/weekly-rollup" element={<ProtectedRoute requiredRole="admin"><AdminFieldAgentWeeklyRollup /></ProtectedRoute>} />
      <Route path="/admin/field-agents/:agent_id/activities/:activity_id" element={<ProtectedRoute requiredRole="admin"><AdminFieldAgentActivityDetail /></ProtectedRoute>} />
      <Route path="/portal/field-agent/weekly-rollup" element={<FieldAgentWeeklyRollup />} />
      <Route path="/portal/field-agent/home" element={<Navigate to="/portal/field-agent/weekly-rollup" replace />} />
      <Route path="/portal/pudo-onboard" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoOnboardLogin /></Suspense>} />
      <Route path="/portal/pudo-onboard/agent" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoOnboardHome /></Suspense>} />
      <Route path="/portal/pudo-onboard/new" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoOnboardWizard /></Suspense>} />
      <Route path="/portal/pudo-onboard/candidate/:id" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoOnboardCandidateDetail /></Suspense>} />
      <Route path="/portal/pudo-onboard/candidate/:id/success" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><PudoOnboardSuccess /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator-login" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoOperatorLogin /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator-staff-login" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoOperatorStaffLogin /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/raise-exception" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full" /></div>}><PudoOperatorRaiseException /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/exceptions" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-rose-500 border-t-transparent rounded-full" /></div>}><PudoOperatorExceptions /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/audit" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><PudoOperatorAudit /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/staff" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><PudoOperatorStaff /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoOperatorHome /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/receive" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoOperatorReceive /></Suspense>} />
      <Route path="/portal/pudo-onboard/operator/release" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" /></div>}><PudoOperatorRelease /></Suspense>} />
      <Route path="/admin/pudo-onboarding" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><AdminPudoOnboarding /></Suspense></AdminRoute>} />
      <Route path="/admin/pudo-onboarding/agents" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><AdminPudoOnboardingAgents /></Suspense></AdminRoute>} />
      <Route path="*" element={<ModernWelcome />} />
      {/* Agent Performance Dashboard */}
      <Route path="/admin/agent-performance" element={<ProtectedRoute requiredRole="admin"><AgentPerformance /></ProtectedRoute>} />
      <Route path="/admin/pudo-stations" element={<ProtectedRoute requiredRole="admin"><AdminPudoStations /></ProtectedRoute>} />
      <Route path="/admin/field-agents" element={<ProtectedRoute requiredRole="admin"><AdminFieldAgents /></ProtectedRoute>} />
      <Route path="/driver/vehicle-log" element={<ProtectedRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><DriverVehicleLog /></Suspense></ProtectedRoute>} />
            <Route path="/admin/vehicle-ops" element={<AdminRoute><Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded-full" /></div>}><AdminVehicleOps /></Suspense></AdminRoute>} />
            <Route path="/admin/business-quick" element={<ProtectedRoute requiredRole="admin"><AdminBusinessQuick /></ProtectedRoute>} />
      <Route path="/hub" element={<Hub />} />
      <Route path="/pin" element={<QuickPin />} />
      <Route path="/portal/karishma" element={<KarishmaPortal />} />
      <Route path="/portal/karishma/payments" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>}><KarishmaPayments /></Suspense>} />
      <Route path="/pin/reset/:token" element={<QuickPinReset />} />
      <Route path="/portal/field-agent/business-quick" element={<FieldAgentBusinessQuick />} />
      <Route path="/admin/field-agents/:id/productivity" element={<ProtectedRoute requiredRole="admin"><AdminFieldAgentProductivity /></ProtectedRoute>} />
      <Route path="/business-quick/login" element={<BusinessQuickLogin />} />
      <Route path="/business-quick/dashboard" element={<BusinessQuickDashboard />} />
      <Route path="/business-quick/book" element={<BusinessQuickBooking />} />
      <Route path="/business-quick/package/:pid" element={<BusinessQuickPackageDetail />} />
      <Route path="/admin/field-activities/review" element={<ProtectedRoute requiredRole="admin"><AdminFieldActivityReviewQueue /></ProtectedRoute>} />
      <Route path="/portal/field-agent/activities/:activity_id" element={<FieldAgentActivityDetail />} />
      <Route path="/admin/disputes-verification" element={<ProtectedRoute requiredRole="admin"><DisputesManagement /></ProtectedRoute>} />
      {/* Assisted Booking KPI */}
      <Route path="/admin/assisted-booking-kpi" element={<ProtectedRoute requiredRole="admin"><AssistedBookingKPI /></ProtectedRoute>} />
      <Route path="/admin/location-intelligence" element={<ProtectedRoute requiredRole="admin"><AdminLocationIntelligence /></ProtectedRoute>} />
      <Route path="/admin/location-capture" element={<ProtectedRoute requiredRole="admin"><AdminLocationCapture /></ProtectedRoute>} />
      {/* Agent Login - PIN based */}
      <Route path="/agent/login" element={<AgentLogin />} />
      {/* Assisted Booking Console */}
      <Route path="/admin/assisted-booking" element={<ProtectedRoute requiredRole="admin"><AssistedBookingConsole /></ProtectedRoute>} />
      <Route path="/admin/assisted-booking-agents" element={<ProtectedRoute requiredRole="admin"><AssistedBookingAgents /></ProtectedRoute>} />
      <Route path="/agent/booking-console" element={<AgentBookingConsole />} />
      {/* R29: Mark's approvals queue for Karishma-proposed drivers/vehicles */}
      <Route path="/admin/approvals" element={<AdminRoute><MarkApprovals /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
    <AuthProvider>
      <ThemeProvider>
        <StaffFunctionsProvider>
        <CurrencyProvider>
        <CartProvider>
          <BrowserRouter>
      <GlobalPwaInstallPrompt />
            <AppRoutes />
            <SetPasswordPrompt />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#f8fafc',
                },
              }}
            />
          </BrowserRouter>
        </CartProvider>
        </CurrencyProvider>
        </StaffFunctionsProvider>
      </ThemeProvider>
    </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
